import { useRef, useState } from 'react';
import {
  Divider,
  Form,
  InlineStack,
  Panel,
  PanelSection,
  Stack,
  TextField,
  useConfirmationModal,
} from '@customerio/pluma-components/react';
import { useWatch, useZodForm, type BaseFormProps } from '@/components/form';
import { isForceDisabled } from '@/services/db/webhook/utils';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import { isEmptyString } from '@/utils/string';
import { useReportingWebhookFormAnalytics } from '../analytics';
import { WebhookEndpointField } from './components/webhook-endpoint-field';
import { WebhookEventsField } from './components/webhook-events-field';
import { WebhookOptionsField } from './components/webhook-options-field';
import { WebhookSaveControls } from './components/webhook-save-controls';
import { WebhookSigningKeyField } from './components/webhook-signing-key-field';
import { WebhookStateField } from './components/webhook-state-field';
import {
  reportingWebhookSchema,
  type ReportingWebhookFormInput,
  type ReportingWebhookFormValues,
} from './reporting-webhook-schema';
import type { ReportingWebhookFormApi, ReportingWebhookSaveAction } from './types';

const ENABLE_CONFIRMATION = {
  title: 'Enable this endpoint?',
  message:
    'If you send a large number of messages, reporting webhook events may spike and overwhelm your endpoint. You can turn webhooks off anytime.',
  confirmLabel: 'Continue to enable',
  cancelLabel: 'Cancel',
};

/** The enabled state each save action asks for. */
function resolveEnabled(action: ReportingWebhookSaveAction, values: ReportingWebhookFormValues): boolean {
  switch (action) {
    case 'save':
      return false;
    case 'save-and-enable':
      return true;
    case 'save-as-is':
      return values.enabled;
    default: {
      const _exhaustive: never = action;
      throw new Error(`Unhandled save action: ${String(_exhaustive)}`);
    }
  }
}

type EnableConfirmationCheck = {
  action: ReportingWebhookSaveAction;
  enabled: boolean;
  events: string[];
  /** The webhook was switched off when the page loaded. */
  wasDisabledOnServer: boolean;
};

/**
 * The warning is about starting a flow of traffic that was not flowing before, so it appears on
 * exactly the saves that begin one: pressing "Save and Enable Webhook", or saving a webhook the
 * server had switched off with the toggle now on. A webhook with no events is left alone — it
 * cannot deliver anything, so nothing starts flowing.
 */
function needsEnableConfirmation({ action, enabled, events, wasDisabledOnServer }: EnableConfirmationCheck): boolean {
  if (action === 'save-and-enable') return true;
  if (action !== 'save-as-is') return false;
  return wasDisabledOnServer && enabled && !isForceDisabled(events);
}

type StateToggleContext = {
  webhookId: string;
  isNewWebhook: boolean;
  isLastEnabledWebhook: boolean;
};

/**
 * The toggle reports intent, not outcome: it fires as the switch moves, so a user who flips it and
 * then abandons the page is still counted.
 */
function trackStateToggle(
  analytics: ReturnType<typeof useReportingWebhookFormAnalytics>,
  isEnabled: boolean,
  { webhookId, isNewWebhook, isLastEnabledWebhook }: StateToggleContext,
): void {
  if (isEnabled) {
    void analytics.trackEnableStarted({ isNewWebhook });
    return;
  }
  void analytics.trackDisableStarted(webhookId, { isLastEnabled: isLastEnabledWebhook });
}

type UnsavedChangesPromptInput = {
  form: ReportingWebhookFormApi;
  isSaving: boolean;
  /** The same save the footer button runs, minus its redirect. Must reject if the save fails. */
  onSave: () => Promise<void>;
  /** Whether "Save & Continue" may be offered at all. */
  canSave: boolean;
};

/**
 * Prompts before leaving with pending edits. Two hooks because `useNavigationGuard` only hears
 * `safeNavigate` and `beforeunload` — browser Back/Forward slips past it and would drop the edits
 * without asking.
 *
 * No `message`: the modal's default copy is already the two lines Ember's `cio-save-continue-modal`
 * shows ("You have unsaved changes." / "Please save your changes or discard them to continue."), and
 * overriding it with a one-liner was what dropped this form out of parity.
 *
 * `onSave` is only offered when the form can actually save. The modal navigates as soon as the
 * promise resolves, so a button that quietly no-ops would carry the user away from their edits —
 * which is why the handler throws rather than returning when it cannot save.
 */
function useUnsavedChangesPrompt({ form, isSaving, onSave, canSave }: UnsavedChangesPromptInput): void {
  const hasUnsavedChanges = () => form.formState.isDirty && !isSaving;

  const handleGuardSave = async () => {
    // Defence in depth: the button is withheld below when the form cannot save, but a stale
    // callback firing after that changed must not resolve into a navigation.
    if (!canSave) {
      throw new Error('Reporting webhook cannot be saved from the leave prompt');
    }

    await onSave();

    // A landed save re-baselines the form, so a still-dirty form means it did not land: schema
    // validation rejected it before the request went out, or the request failed and the action
    // component reported it. Either way the modal must not navigate over the edits, and only this
    // post-condition catches both — the save path resolves in both cases rather than throwing.
    if (form.formState.isDirty) {
      throw new Error('Reporting webhook save did not complete');
    }
  };

  const guardOptions = { hasUnsavedChanges, onSave: canSave ? handleGuardSave : undefined };

  useNavigationGuard(guardOptions);
  useHistoryLeaveBlocker(guardOptions);
}

type FormFlagInput = {
  isSaving: boolean;
  readOnly: boolean;
  events: string[];
  endpointUrl: string;
  errorCount: number;
  isDirty: boolean;
};

/**
 * The interlocking disabled rules, derived in one place. "Save" needs a clean, dirty form with an
 * endpoint; "Save and Enable" additionally needs at least one event, because a webhook with none
 * cannot be turned on.
 */
function deriveFormFlags({ isSaving, readOnly, events, endpointUrl, errorCount, isDirty }: FormFlagInput) {
  const isFormDisabled = isSaving || readOnly;
  const hasNoEvents = isForceDisabled(events);
  const isSaveDisabled = isFormDisabled || errorCount > 0 || !isDirty || isEmptyString(endpointUrl);

  return {
    isFormDisabled,
    hasNoEvents,
    isSaveDisabled,
    isActionDisabled: (action: ReportingWebhookSaveAction) =>
      isSaveDisabled || (action === 'save-and-enable' && hasNoEvents),
  };
}

type SubmitArgs = {
  action: ReportingWebhookSaveAction;
  values: ReportingWebhookFormValues;
  form: ReportingWebhookFormApi;
  confirm: ReturnType<typeof useConfirmationModal>;
  onSubmit: ReportingWebhookFormProps['onSubmit'];
  wasDisabledOnServer: boolean;
  isActionDisabled: (action: ReportingWebhookSaveAction) => boolean;
  isSubmittingRef: { current: boolean };
};

/**
 * The save flow: re-check the disabled conditions (the Enter key reaches this without a button),
 * apply the action's enabled state, confirm when the save would start traffic flowing, and hold a
 * synchronous latch so a second activation is dropped rather than queued.
 */
async function submitReportingWebhook({
  action,
  values,
  form,
  confirm,
  onSubmit,
  wasDisabledOnServer,
  isActionDisabled,
  isSubmittingRef,
}: SubmitArgs): Promise<void> {
  if (isActionDisabled(action) || isSubmittingRef.current) return;

  const nextEnabled = resolveEnabled(action, values);
  form.setValue('enabled', nextEnabled, { shouldDirty: true });

  if (needsEnableConfirmation({ action, enabled: nextEnabled, events: values.events, wasDisabledOnServer })) {
    const isConfirmed = await confirm(ENABLE_CONFIRMATION, { shouldCloseOnUnmount: true });
    if (isConfirmed !== true) {
      // Backing out of the warning leaves the webhook off rather than saving it enabled.
      form.setValue('enabled', false, { shouldDirty: true });
      return;
    }
  }

  isSubmittingRef.current = true;
  try {
    await onSubmit({ ...values, enabled: nextEnabled }, form);
  } finally {
    isSubmittingRef.current = false;
  }
}

export type ReportingWebhookFormProps = BaseFormProps<ReportingWebhookFormInput, ReportingWebhookFormValues> & {
  /**
   * The same save as `onSubmit`, minus the redirect to the list. Used by the leave guard, which is
   * already holding the destination the user asked for: `safeNavigate` keeps a single
   * pending-transition subscription, so a redirect here would replace theirs and land them on the
   * list instead of where they were going.
   */
  onSaveAndStay: BaseFormProps<ReportingWebhookFormInput, ReportingWebhookFormValues>['onSubmit'];
  /**
   * The webhook's persisted signing key, empty until it has been created. A key is only ever issued
   * on creation, so its absence is what tells the form it is building a new webhook rather than
   * editing one — the route it was reached by is not consulted.
   */
  signingKey: string;
  /** Needed to rotate the signing key and to identify the webhook in analytics. Empty on create. */
  webhookId: string;
  /** Endpoint URLs already claimed by other webhooks, for the duplicate notice. */
  otherEndpointUrls: readonly string[];
  /** Switching this webhook off would leave the workspace with none enabled. */
  isLastEnabledWebhook: boolean;
  /** The viewer may look but not save. Renders every control inert; it does not redirect. */
  readOnly: boolean;
  isSaving: boolean;
  cancelHref: string;
};

/**
 * Everything the form needs before it can draw a single field: the RHF instance, the watched values,
 * the derived disabled rules, and the handlers. Kept apart from the component so the component below
 * is only the field layout — the two have nothing to say to each other beyond this return value.
 */
function useReportingWebhookFormState({
  onSubmit,
  onSaveAndStay,
  defaultValues,
  signingKey,
  webhookId,
  otherEndpointUrls,
  isLastEnabledWebhook,
  readOnly,
  isSaving,
}: ReportingWebhookFormProps) {
  const form: ReportingWebhookFormApi = useZodForm({
    schema: reportingWebhookSchema,
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const confirm = useConfirmationModal();
  const analytics = useReportingWebhookFormAnalytics();

  const isNewWebhook = isEmptyString(signingKey);

  // The state the server last gave us, read once. A live read of the toggle could not tell an
  // enable apart from a webhook that was already on when the page opened.
  const [wasDisabledOnServer] = useState(() => defaultValues.enabled !== true);

  // A second click landing before React re-renders would still see the old isSaving, so the guard
  // has to be something that flips synchronously.
  const isSubmittingRef = useRef(false);

  const enabled = useWatch({ control: form.control, name: 'enabled' });
  const endpointUrl = useWatch({ control: form.control, name: 'endpoint_url' });
  const events = useWatch({ control: form.control, name: 'events' });

  const { isFormDisabled, hasNoEvents, isSaveDisabled, isActionDisabled } = deriveFormFlags({
    isSaving,
    readOnly,
    events,
    endpointUrl,
    errorCount: Object.keys(form.formState.errors).length,
    isDirty: form.formState.isDirty,
  });

  const runSave = (action: ReportingWebhookSaveAction, submit = onSubmit) =>
    form.handleSubmit((values) =>
      submitReportingWebhook({
        action,
        values,
        form,
        confirm,
        onSubmit: submit,
        wasDisabledOnServer,
        isActionDisabled,
        isSubmittingRef,
      }),
    );

  // Creating saves the webhook switched off; editing saves whatever the state toggle says. Either
  // way this is the form's own submit, so the Enter key lands on it.
  const submitAction: ReportingWebhookSaveAction = isNewWebhook ? 'save' : 'save-as-is';

  // Saving from here would raise the enable warning, which is a decision of its own — and the leave
  // modal holds itself non-dismissable while its save runs, so that confirmation would open on top
  // of a modal the user cannot escape. Backing out of it is worse still: it switches `enabled` off
  // and skips the save, so they would land back on the form with the toggle silently reverted.
  // Withhold "Save & Continue" instead and let the footer button, which hosts the warning properly,
  // be the way to enable.
  const needsEnableWarning = needsEnableConfirmation({
    action: submitAction,
    enabled,
    events,
    wasDisabledOnServer,
  });

  useUnsavedChangesPrompt({
    form,
    isSaving,
    onSave: () => runSave(submitAction, onSaveAndStay)(),
    canSave: !isSaveDisabled && !needsEnableWarning,
  });

  return {
    form,
    nameField: form.registerPluma('name'),
    isNewWebhook,
    enabled,
    isFormDisabled,
    hasNoEvents,
    isSaveDisabled,
    hasDuplicateEndpoint: otherEndpointUrls.includes(endpointUrl),
    submitAction,
    runSave,
    handleEnabledChange: (isEnabled: boolean) => {
      form.setValue('enabled', isEnabled, { shouldDirty: true });
      trackStateToggle(analytics, isEnabled, { webhookId, isNewWebhook, isLastEnabledWebhook });
    },
    // Typing only records the value; the message waits for blur so a half-typed name is not
    // reported as too short.
    handleNameInput: (event: { target: { value: string } }) => {
      form.setValue('name', event.target.value, { shouldDirty: true, shouldTouch: true });
    },
    handleKeyCopied: () => {
      void analytics.trackKeyCopied(webhookId);
    },
  };
}

/**
 * The create and edit form for a reporting webhook. Both flows render the same fields; the action
 * component supplies the defaults, the save, and the surrounding page.
 */
export function ReportingWebhookForm(props: ReportingWebhookFormProps) {
  const { signingKey, webhookId, isSaving, cancelHref } = props;
  const {
    form,
    nameField,
    isNewWebhook,
    enabled,
    isFormDisabled,
    hasNoEvents,
    isSaveDisabled,
    hasDuplicateEndpoint,
    submitAction,
    runSave,
    handleEnabledChange,
    handleNameInput,
    handleKeyCopied,
  } = useReportingWebhookFormState(props);

  return (
    <Form onSubmit={runSave(submitAction)} data-test-webhook-form>
      <Panel mb="300">
        <PanelSection>
          {isNewWebhook ? null : (
            <>
              <WebhookStateField
                isEnabled={enabled}
                isForceDisabled={hasNoEvents}
                isDisabled={isFormDisabled}
                onChange={handleEnabledChange}
              />
              <Divider my="300" />
            </>
          )}

          <Stack gap="300">
            <TextField
              {...nameField}
              onInput={handleNameInput}
              data-test-webhook-name
              label="Webhook name"
              placeholder="e.g. Order events"
              autofocus
              isDisabled={isFormDisabled}
            />

            <WebhookEndpointField
              form={form}
              signingKey={signingKey}
              webhookId={webhookId}
              isDisabled={isFormDisabled}
              hasDuplicateEndpoint={hasDuplicateEndpoint}
            />

            {isNewWebhook ? null : (
              <WebhookSigningKeyField
                webhookId={webhookId}
                signingKey={signingKey}
                isDisabled={isFormDisabled}
                onCopy={handleKeyCopied}
              />
            )}
          </Stack>

          <Divider my="300" />

          <InlineStack alignBlock="flex-start" alignInline="space-between" gap="300">
            <WebhookEventsField form={form} isDisabled={isFormDisabled} isNewWebhook={isNewWebhook} />
            <WebhookOptionsField form={form} isDisabled={isFormDisabled} />
          </InlineStack>
        </PanelSection>
      </Panel>

      <WebhookSaveControls
        isNewWebhook={isNewWebhook}
        isSaveDisabled={isSaveDisabled}
        isForceDisabled={hasNoEvents}
        isSaving={isSaving}
        cancelHref={cancelHref}
        onSaveAndEnable={() => {
          void runSave('save-and-enable')();
        }}
      />
    </Form>
  );
}
